import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useCallback, useMemo } from "react";
import { ArrowRight } from "lucide-react";
import type { SavedDestination } from "@mtform/shared";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { AddressField } from "@/components/form/AddressField";
import { AddressPickHint } from "@/components/form/AddressPickHint";
import { AddressConfirmedHint } from "@/components/form/AddressConfirmedHint";
import { LocationsPicker } from "@/components/form/LocationsPicker";
import { SavedValuePicker } from "@/components/form/SavedValuePicker";
import { fullAddressFromPlace, joinAddressLine } from "@/lib/format";
import { WizardCard } from "./WizardCard";
import { usePatientLookupState } from "./PatientLookupContext";
import type { TripFormValues } from "./schema";
import type { WizardStepStatus } from "./WizardCard";

interface DropoffSectionProps {
  index: number;
  status: WizardStepStatus;
  onContinue: () => void;
  onEdit: () => void;
}

export function DropoffSection({
  index,
  status,
  onContinue,
  onEdit,
}: DropoffSectionProps) {
  const {
    control,
    register,
    setValue,
    formState: { errors },
    trigger,
  } = useFormContext<TripFormValues>();

  const lookup = usePatientLookupState();
  const destinationAddress =
    useWatch({ control, name: "destinationAddress" }) ?? "";
  const destinationCityZip =
    useWatch({ control, name: "destinationCityZip" }) ?? "";
  const destinationPlace = useWatch({ control, name: "destinationPlace" });

  const allSavedDestinations =
    lookup.status === "found" && lookup.patient
      ? lookup.patient.savedDestinations
      : [];
  const savedDestinations = useMemo(
    () =>
      allSavedDestinations.filter(
        (s) =>
          normalizeText(s.address) !== normalizeText(destinationAddress) ||
          normalizeText(s.cityZip) !== normalizeText(destinationCityZip),
      ),
    [allSavedDestinations, destinationAddress, destinationCityZip],
  );

  const renderSavedDestination = useCallback(
    (s: SavedDestination) => ({
      primary: s.address,
      meta: s.cityZip || undefined,
    }),
    [],
  );

  const onSelectSavedDestination = useCallback(
    (s: SavedDestination) => {
      setValue("destinationAddress", s.address, {
        shouldDirty: true,
        shouldTouch: true,
      });
      if (s.cityZip)
        setValue("destinationCityZip", s.cityZip, { shouldDirty: true });
      setValue(
        "destinationPlace",
        {
          lat: s.lat,
          lon: s.lon,
          buildingNumber: "",
          line1: "",
          city: "",
          state: "",
          postalCode: "",
          formatted: s.address,
        },
        { shouldDirty: true },
      );
      void trigger(["destinationAddress", "destinationCityZip"]);
    },
    [setValue, trigger],
  );

  // See PickupSection: "Location confirmed" when we have coordinates, an amber
  // pick-from-dropdown nudge when we don't (the server backfills as a safety
  // net, so it never blocks the booking). The two states are mutually exclusive.
  const hasDestinationCoords = Boolean(
    destinationPlace &&
      destinationPlace.lat != null &&
      destinationPlace.lon != null &&
      Number.isFinite(destinationPlace.lat) &&
      Number.isFinite(destinationPlace.lon),
  );
  const hasDestinationAddress = destinationAddress.trim().length > 0;

  async function handleContinue() {
    const ok = await trigger(["destinationAddress"]);
    if (!ok) return;
    onContinue();
  }

  if (status === "complete") {
    const summary =
      joinAddressLine(destinationAddress, destinationCityZip) || "—";
    return (
      <WizardCard
        index={index}
        title="Drop-off"
        status="complete"
        summary={summary}
        onEdit={onEdit}
      />
    );
  }

  return (
    <WizardCard
      index={index}
      title="Drop-off"
      description="Where we need to take the patient."
      status={status}
    >
      <div className="flex flex-col gap-1.5">
        <Field
          label="Destination / building"
          required
          htmlFor="destinationAddress"
          error={errors.destinationAddress?.message}
        >
          <Controller
            control={control}
            name="destinationAddress"
            render={({ field }) => (
              <AddressField
                value={field.value ?? ""}
                onChange={field.onChange}
                onPlace={(place, cityZip) => {
                  // Full canonical address — see PickupSection / fullAddressFromPlace.
                  field.onChange(fullAddressFromPlace(place, field.value ?? ""));
                  setValue("destinationPlace", place, { shouldDirty: true });
                  if (cityZip) setValue("destinationCityZip", cityZip, { shouldDirty: true });
                  void trigger(["destinationAddress"]);
                }}
                onClearPlace={() => {
                  setValue("destinationPlace", null, { shouldDirty: true });
                  setValue("destinationCityZip", "", { shouldDirty: true });
                }}
                placeholder="Hospital, building, or full address"
                maxLength={200}
                invalid={Boolean(errors.destinationAddress)}
              />
            )}
          />
        </Field>
        <div className="flex flex-wrap items-center gap-1.5">
          <LocationsPicker
            mode="dropoff"
            onSelect={(picked) => {
              const loc = picked.location;
              setValue("destinationAddress", picked.address || picked.label, {
                shouldDirty: true,
                shouldTouch: true,
              });
              setValue("destinationCityZip", loc.cityZip, { shouldDirty: true });
              setValue(
                "destinationPlace",
                {
                  lat: loc.lat,
                  lon: loc.lon,
                  buildingNumber: "",
                  line1: loc.addressLine,
                  city: "",
                  state: "",
                  postalCode: "",
                  formatted: picked.address || picked.label,
                },
                { shouldDirty: true },
              );
              void trigger(["destinationAddress", "destinationCityZip"]);
            }}
          />
          {savedDestinations.length > 0 ? (
            <SavedValuePicker
              label="Saved drop-offs"
              items={savedDestinations}
              renderItem={renderSavedDestination}
              onSelect={onSelectSavedDestination}
              description="Click a place to fill the drop-off address fields."
            />
          ) : null}
        </div>
      </div>

      {hasDestinationAddress ? (
        hasDestinationCoords ? <AddressConfirmedHint /> : <AddressPickHint />
      ) : null}

      <Field label="Drop-off notes (optional)" htmlFor="dropoffNotes">
        <Textarea
          id="dropoffNotes"
          rows={2}
          placeholder="Unit/department, receiving staff, special instructions…"
          maxLength={1000}
          {...register("dropoffNotes")}
        />
      </Field>

      <div className="flex justify-end pt-1">
        <Button type="button" onClick={handleContinue} className="min-w-[10rem]">
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </WizardCard>
  );
}

function normalizeText(value: string | null | undefined): string {
  return String(value ?? "").trim().toLowerCase();
}
